import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  computed,
  effect,
  inject,
  input,
  viewChild,
} from '@angular/core';
import { CommunityLink } from '../../interfaces';
import { LinkBackgroundPipe } from '../../pipes/link-background.pipe';
import { DataTestDirective } from '@outlaw/core';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';

const MIN_SCROLL_DELAY = 300;
const SCROLL_LEFT_THRESHOLD = 1;
const SCROLL_RIGHT_THRESHOLD = 5;
const DEFAULT_GAP = 12;
const MIN_DRAG_DISTANCE = 5;
const MOMENTUM_DAMPING = 0.95;
const MOMENTUM_MIN_VELOCITY = 0.5;

const DEFAULT_SLIDE_WIDTH = {
  horizontal: 160,
  vertical: 120,
};

const SKELETON_COUNT = {
  horizontal: 5,
  vertical: 6,
};

const SKELETON_HEIGHT = {
  horizontal: '100px',
  vertical: '150px',
};

const SKELETON_WIDTH = {
  horizontal: '160px',
  vertical: '120px',
};

@Component({
  selector: 'app-community-links',
  templateUrl: './community-links.component.html',
  styleUrls: ['./community-links.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [LinkBackgroundPipe, DataTestDirective, NgxSkeletonLoaderModule],
})
export class CommunityLinksComponent implements OnInit {
  links = input<CommunityLink[]>([]);
  isVertical = input<boolean>(false);
  isLoading = input<boolean>(false);
  sliderContainer = viewChild<ElementRef<HTMLDivElement>>('sliderContainer');

  showPrev = false;
  showNext = false;
  canScroll = false;

  protected skeletonItems = computed(() =>
    Array(this.isVertical() ? SKELETON_COUNT.vertical : SKELETON_COUNT.horizontal).fill(null)
  );

  protected skeletonHeight = computed(() =>
    this.isVertical() ? SKELETON_HEIGHT.vertical : SKELETON_HEIGHT.horizontal
  );

  protected skeletonWidth = computed(() => (this.isVertical() ? SKELETON_WIDTH.vertical : SKELETON_WIDTH.horizontal));

  private scrollTimeout: number | null = null;

  private isDragging = false;
  private startX = 0;
  private scrollLeft = 0;
  private dragStartTime = 0;
  private lastDragX = 0;
  private velocity = 0;
  private momentumAnimationId: number | null = null;
  private preventClick = false;

  private destroyRef = inject(DestroyRef);
  private cdr = inject(ChangeDetectorRef);

  constructor() {
    effect(() => {
      if (this.sliderContainer()) {
        const container = this.sliderContainer().nativeElement;
        container.scrollLeft = 0;
        this.updateNavigationState();
        this.setupDragListeners(container);
      }
    });
  }

  ngOnInit(): void {
    if (this.links().length > 0) {
      requestAnimationFrame(() => this.updateNavigationState());
    }
  }

  private setupDragListeners(container: HTMLElement): void {
    container.addEventListener('mousedown', this.handleMouseDown.bind(this));
    container.addEventListener('mousemove', this.handleMouseMove.bind(this));
    container.addEventListener('mouseup', this.handleMouseUp.bind(this));
    container.addEventListener('mouseleave', this.handleMouseLeave.bind(this));

    container.addEventListener('touchstart', this.handleTouchStart.bind(this), {
      passive: true,
    });
    container.addEventListener('touchmove', this.handleTouchMove.bind(this), {
      passive: false,
    });
    container.addEventListener('touchend', this.handleTouchEnd.bind(this));

    container.addEventListener('click', this.handleClick.bind(this), true);

    this.destroyRef.onDestroy(() => this.cleanupDragListeners(container));
  }

  private cleanupDragListeners(container: HTMLElement): void {
    container.removeEventListener('mousedown', this.handleMouseDown.bind(this));
    container.removeEventListener('mousemove', this.handleMouseMove.bind(this));
    container.removeEventListener('mouseup', this.handleMouseUp.bind(this));
    container.removeEventListener('mouseleave', this.handleMouseLeave.bind(this));

    container.removeEventListener('touchstart', this.handleTouchStart.bind(this));
    container.removeEventListener('touchmove', this.handleTouchMove.bind(this));
    container.removeEventListener('touchend', this.handleTouchEnd.bind(this));

    container.removeEventListener('click', this.handleClick.bind(this));

    if (this.momentumAnimationId) {
      cancelAnimationFrame(this.momentumAnimationId);
    }
  }

  private handleMouseDown(e: MouseEvent): void {
    const container = e.currentTarget as HTMLElement;
    this.startDrag(e.pageX, container);
    container.style.cursor = 'grabbing';
    e.preventDefault();
  }

  private handleMouseMove(e: MouseEvent): void {
    if (!this.isDragging) return;
    const container = e.currentTarget as HTMLElement;
    this.performDrag(e.pageX, container);
    e.preventDefault();
  }

  private handleMouseUp(e: MouseEvent): void {
    if (!this.isDragging) return;
    const container = e.currentTarget as HTMLElement;
    this.endDrag(container);
    container.style.cursor = 'grab';
  }

  private handleMouseLeave(e: MouseEvent): void {
    if (!this.isDragging) return;
    const container = e.currentTarget as HTMLElement;
    this.endDrag(container);
    container.style.cursor = 'grab';
  }

  private handleTouchStart(e: TouchEvent): void {
    const container = e.currentTarget as HTMLElement;
    const touch = e.touches[0];
    this.startDrag(touch.pageX, container);
  }

  private handleTouchMove(e: TouchEvent): void {
    if (!this.isDragging) return;
    const container = e.currentTarget as HTMLElement;
    const touch = e.touches[0];
    this.performDrag(touch.pageX, container);
    e.preventDefault();
  }

  private handleTouchEnd(e: TouchEvent): void {
    if (!this.isDragging) return;
    const container = e.currentTarget as HTMLElement;
    this.endDrag(container);
  }

  private handleClick(e: MouseEvent): void {
    if (this.preventClick) {
      e.preventDefault();
      e.stopPropagation();
      this.preventClick = false;
    }
  }

  private startDrag(pageX: number, container: HTMLElement): void {
    this.isDragging = true;
    this.startX = pageX - container.offsetLeft;
    this.scrollLeft = container.scrollLeft;
    this.dragStartTime = Date.now();
    this.lastDragX = pageX;
    this.velocity = 0;

    if (this.momentumAnimationId) {
      cancelAnimationFrame(this.momentumAnimationId);
      this.momentumAnimationId = null;
    }

    container.style.scrollSnapType = 'none';
    container.style.scrollBehavior = 'auto';
  }

  private performDrag(pageX: number, container: HTMLElement): void {
    const x = pageX - container.offsetLeft;
    const walk = (x - this.startX) * 1.2;
    const newScrollLeft = this.scrollLeft - walk;

    const currentTime = Date.now();
    const timeDiff = currentTime - this.dragStartTime;

    if (timeDiff > 0) {
      this.velocity = ((pageX - this.lastDragX) / timeDiff) * 16;
    }

    this.lastDragX = pageX;
    this.dragStartTime = currentTime;

    container.scrollLeft = newScrollLeft;
    this.updateNavigationState();
  }

  private endDrag(container: HTMLElement): void {
    this.isDragging = false;

    const dragDistance = Math.abs(this.scrollLeft - container.scrollLeft);
    if (dragDistance > MIN_DRAG_DISTANCE) {
      this.preventClick = true;
      setTimeout(() => {
        this.preventClick = false;
      }, 100);
    }

    this.applyMomentum(container);

    // Wait for momentum to finish before snapping
    setTimeout(() => {
      this.snapToNearestSlide(container);
      // Re-enable scroll snap after positioning is complete
      setTimeout(() => {
        container.style.scrollSnapType = 'x mandatory';
        container.style.scrollBehavior = 'smooth';
      }, 50);
    }, 100);
  }

  private applyMomentum(container: HTMLElement): void {
    if (Math.abs(this.velocity) < MOMENTUM_MIN_VELOCITY) return;

    const animate = () => {
      if (Math.abs(this.velocity) > MOMENTUM_MIN_VELOCITY) {
        container.scrollLeft -= this.velocity;
        this.velocity *= MOMENTUM_DAMPING;

        const maxScrollLeft = container.scrollWidth - container.offsetWidth;
        if (container.scrollLeft <= 0 || container.scrollLeft >= maxScrollLeft) {
          this.velocity = 0;
        }

        this.updateNavigationState();
        this.momentumAnimationId = requestAnimationFrame(animate);
      } else {
        this.momentumAnimationId = null;
      }
    };

    this.momentumAnimationId = requestAnimationFrame(animate);
  }

  private snapToNearestSlide(container: HTMLElement): void {
    const slides = container.querySelectorAll('.link');
    if (!slides.length) return;

    const scrollLeft = container.scrollLeft;
    const containerWidth = container.offsetWidth;

    // Find the most visible slide instead of the one closest to center
    let mostVisibleSlide: Element | null = null;
    let maxVisibility = 0;

    slides.forEach(slide => {
      const slideElement = slide as HTMLElement;
      const slideLeft = slideElement.offsetLeft;
      const slideWidth = slideElement.offsetWidth;
      const slideRight = slideLeft + slideWidth;

      // Calculate how much of the slide is visible
      const visibleLeft = Math.max(slideLeft, scrollLeft);
      const visibleRight = Math.min(slideRight, scrollLeft + containerWidth);
      const visibleWidth = Math.max(0, visibleRight - visibleLeft);
      const visibility = visibleWidth / slideWidth;

      if (visibility > maxVisibility) {
        maxVisibility = visibility;
        mostVisibleSlide = slide;
      }
    });

    if (mostVisibleSlide) {
      const slideElement = mostVisibleSlide as HTMLElement;
      const targetScroll = slideElement.offsetLeft;

      // Check if we're at the boundaries
      const maxScroll = container.scrollWidth - container.offsetWidth;
      const finalScroll = Math.max(0, Math.min(targetScroll, maxScroll));

      container.scrollTo({
        left: finalScroll,
        behavior: 'smooth',
      });

      this.scheduleNavigationUpdate();
    }
  }

  onPrevClick(): void {
    const container = this.sliderContainer().nativeElement;
    this.scrollToPrevious(container);
  }

  onNextClick(): void {
    const container = this.sliderContainer().nativeElement;
    this.scrollToNext(container);
  }

  private scrollToPrevious(container: HTMLElement): void {
    const scrollStep = this.getScrollStep();
    const targetPosition = Math.max(0, container.scrollLeft - scrollStep);

    this.showNext = true;
    if (targetPosition <= SCROLL_LEFT_THRESHOLD) {
      this.showPrev = false;
    }

    container.scrollTo({
      left: targetPosition,
      behavior: 'smooth',
    });

    this.scheduleNavigationUpdate();
  }

  private scrollToNext(container: HTMLElement): void {
    const scrollStep = this.getScrollStep();
    const maxScrollLeft = container.scrollWidth - container.offsetWidth;
    const targetPosition = Math.min(maxScrollLeft, container.scrollLeft + scrollStep);

    this.showPrev = true;
    if (targetPosition >= maxScrollLeft - SCROLL_RIGHT_THRESHOLD) {
      this.showNext = false;
    }

    container.scrollTo({
      left: targetPosition,
      behavior: 'smooth',
    });

    this.scheduleNavigationUpdate();
  }

  private scheduleNavigationUpdate(): void {
    setTimeout(() => this.updateNavigationState(), MIN_SCROLL_DELAY);
  }

  onScroll(): void {
    if (this.scrollTimeout) {
      clearTimeout(this.scrollTimeout);
    }

    this.scrollTimeout = window.setTimeout(() => this.updateNavigationState(), MIN_SCROLL_DELAY);
  }

  private getScrollStep(): number {
    const container = this.sliderContainer()?.nativeElement;

    if (!container) {
      return this.isVertical() ? DEFAULT_SLIDE_WIDTH.vertical : DEFAULT_SLIDE_WIDTH.horizontal;
    }

    const firstSlide = container.querySelector('.link') as HTMLElement;
    if (!firstSlide) {
      return this.isVertical() ? DEFAULT_SLIDE_WIDTH.vertical : DEFAULT_SLIDE_WIDTH.horizontal;
    }

    const slideWidth = firstSlide.offsetWidth;
    const computedStyle = getComputedStyle(container);
    const gap = parseInt(computedStyle.gap) || DEFAULT_GAP;

    return slideWidth + gap;
  }

  private updateNavigationState(): void {
    const container = this.sliderContainer()?.nativeElement;
    if (!container) return;

    this.canScroll = this.checkCanScroll(container);
    this.showPrev = this.checkCanScrollLeft(container);
    this.showNext = this.checkCanScrollRight(container);

    this.cdr.markForCheck();
  }

  private checkCanScroll(container: HTMLElement): boolean {
    return container.scrollWidth > container.offsetWidth;
  }

  private checkCanScrollLeft(container: HTMLElement): boolean {
    return Math.round(container.scrollLeft) > SCROLL_LEFT_THRESHOLD;
  }

  private checkCanScrollRight(container: HTMLElement): boolean {
    const scrollLeft = Math.round(container.scrollLeft);
    const maxScrollLeft = container.scrollWidth - container.offsetWidth;
    return scrollLeft < maxScrollLeft - SCROLL_RIGHT_THRESHOLD;
  }

  isDragActive(): boolean {
    return this.isDragging;
  }
}
